import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/platform/authentication/hooks/useAuth';
import { fetchP1InterruptionDecision } from '@/capabilities/service-operations/api/resolve';
import {
  hasHandledP1Interruption,
  p1InterruptionHref,
  rememberP1Interruption,
} from './p1InterruptionContext';

/**
 * Global navigation bridge for server-selected P1 interruptions. It never
 * derives eligibility from client roles or severity and never selects a target
 * from nearby data. The exact current route is embedded as parked work.
 */
export function P1InterruptionNavigator(): null {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const inMemoryHandled = useRef(new Set<string>());
  const scope = useMemo(
    () => ({ tenantId: user?.tenantId ?? '', actorId: user?.id ?? '' }),
    [user?.id, user?.tenantId]
  );
  const decisionQuery = useQuery({
    queryKey: ['resolve', 'p1-interruption', scope.tenantId, scope.actorId],
    queryFn: fetchP1InterruptionDecision,
    enabled: Boolean(scope.tenantId && scope.actorId),
    refetchInterval: (query) => (query.state.data?.policy.enabled === false ? false : 5_000),
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    inMemoryHandled.current.clear();
  }, [scope.actorId, scope.tenantId]);

  useEffect(() => {
    const decision = decisionQuery.data;
    const itemId = decision?.item?.id;
    if (!decision || !itemId) return;
    if (
      inMemoryHandled.current.has(itemId) ||
      hasHandledP1Interruption(scope, itemId) ||
      (location.pathname === '/workbench' &&
        new URLSearchParams(location.search).get('queueItem') === itemId)
    ) {
      return;
    }
    const href = p1InterruptionHref(decision, location);
    if (!href) return;
    inMemoryHandled.current.add(itemId);
    rememberP1Interruption(scope, itemId);
    navigate(href);
  }, [decisionQuery.data, location, navigate, scope]);

  return null;
}
